/**
 * YearMismatchWarning Component
 *
 * UX-004: Shows a warning when the selected calculation year
 * doesn't match the years for which import data is available.
 *
 * Provides clear actions: switch to a year with data or import new data.
 */

import { Alert, Button, Group, Text, Badge } from '@mantine/core';
import { IconAlertTriangle, IconCalendar, IconUpload } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export interface YearMismatchWarningProps {
  /** Currently selected year in the calculator */
  selectedYear: number;
  /** Years that have hourly import data available */
  availableYears: number[];
  /** Callback to change the selected year */
  onYearChange: (year: number) => void;
}

/**
 * Warning displayed when calculator year doesn't match import data years
 */
export function YearMismatchWarning({
  selectedYear,
  availableYears,
  onYearChange,
}: YearMismatchWarningProps) {
  const navigate = useNavigate();

  // Find the closest year with data
  const closestYear = availableYears.reduce((prev, curr) =>
    Math.abs(curr - selectedYear) < Math.abs(prev - selectedYear) ? curr : prev
  , availableYears[0]);

  // If there's data for the selected year, don't show warning
  if (availableYears.includes(selectedYear)) {
    return null;
  }

  // If there's no import data at all, don't show this warning
  // (DataSourcesAvailability component handles that case)
  if (availableYears.length === 0) {
    return null;
  }

  return (
    <Alert
      icon={<IconAlertTriangle size={18} />}
      color="yellow"
      variant="light"
      title="Brak danych z importu dla wybranego roku"
      mb="md"
    >
      <Text size="sm" mb="sm">
        Wybrałeś rok <strong>{selectedYear}</strong>, ale dostępne dane z importu Tauron dotyczą:
      </Text>
      <Group gap="xs" mb="md">
        {availableYears.sort((a, b) => b - a).map(year => (
          <Badge
            key={year}
            color="green"
            variant="light"
            leftSection={<IconCalendar size={12} />}
          >
            {year}
          </Badge>
        ))}
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        Wzorzec "Z raportu Tauron" będzie niedostępny dla roku {selectedYear}.
        Możesz użyć wzorców standardowych lub zmienić rok kalkulacji.
      </Text>
      <Group gap="sm">
        <Button
          size="xs"
          variant="light"
          color="blue"
          leftSection={<IconCalendar size={14} />}
          onClick={() => onYearChange(closestYear)}
        >
          Użyj roku {closestYear}
        </Button>
        <Button
          size="xs"
          variant="subtle"
          leftSection={<IconUpload size={14} />}
          onClick={() => navigate('/konto/import')}
        >
          Importuj dane dla {selectedYear}
        </Button>
      </Group>
    </Alert>
  );
}
